/**
 * The cancel window's flow for a booking's page, one home for the Mini App and the studio website
 * (shared-client-screens D3, rules 12 and 13): opening the window reads the booking again whenever
 * a refund rule applies, so the line shown is the one in force now; a read that fails says so
 * instead; the window closes while the cancel goes; what was cancelled (booking, request,
 * waitlist) is held from the tap, since the booking reads cancelled once it is done.
 *
 * Each side passes its own reads: the booking, its refetch, and its cancel mutation.
 */
import { useCallback, useRef, useState } from 'react';

import type { BookingDetailCancel } from '../../pages/BookingDetailPage/BookingDetailView';
import { cancelKindOf, needsFreshRefundPolicy, type CancelKind } from '../../pages/BookingDetailPage/bookingDetailRows';
import type { BookingDetail } from '../../types/bookingDetail';

/** A cancel mutation: the app's useCancelBooking or the website's useWebCancelBooking. */
export interface BookingCancelMutation {
  isPending: boolean;
  mutate: (variables: void, options: { onSuccess: () => void; onError: () => void }) => void;
}

export interface BookingCancelFlowInput {
  booking: BookingDetail | undefined;
  /** Reads the booking again (its refund rule now). */
  refetch: () => Promise<{ isError: boolean; data?: BookingDetail }>;
  cancel: BookingCancelMutation;
  /** The cancel failed (the booking may have changed under the page). */
  onFailed: () => void;
  onCancelled?: () => void;
}

export function useBookingCancelFlow({ booking, refetch, cancel, onFailed, onCancelled }: BookingCancelFlowInput): BookingDetailCancel {
  const [open, setOpen] = useState(false);
  const [line, setLine] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState<CancelKind | null>(null);
  const request = useRef(0);
  const kind = useRef<CancelKind>('booking');
  const onDone = useCallback(() => setSuccess(null), []);

  const onOpen = async () => {
    if (!booking) return;
    const read = ++request.current;
    setOpen(true);
    setLine(null);
    setError(false);
    const fresh = needsFreshRefundPolicy(booking);
    setLoading(fresh);
    if (!fresh) return;
    try {
      const result = await refetch();
      if (read !== request.current) return;
      if (result.isError || !result.data?.canCancel || !result.data.refundPolicySummaryLine) {
        setError(true);
        return;
      }
      setLine(result.data.refundPolicySummaryLine);
    } catch {
      if (read === request.current) setError(true);
    } finally {
      if (read === request.current) setLoading(false);
    }
  };

  const onClose = () => {
    request.current += 1;
    setOpen(false);
  };

  const onConfirm = () => {
    if (!booking || cancel.isPending) return;
    request.current += 1;
    kind.current = cancelKindOf(booking);
    setOpen(false);
    cancel.mutate(undefined, {
      onSuccess: () => {
        setSuccess(kind.current);
        onCancelled?.();
      },
      onError: onFailed,
    });
  };

  return {
    open,
    policy: { loading, error, line },
    pending: cancel.isPending,
    success,
    onOpen: () => void onOpen(),
    onClose,
    onConfirm,
    onDone,
  };
}
